import { Text } from "@chakra-ui/react";
import { byLook } from "theme/look";
import { LegacyBarChart } from "./legacy/BarChart";
import { isPopulatedArray, toNumberDisplayValue } from "@intentsify/utils";
import { defineChart } from "@tanstack/charts";
import { tooltip } from "@tanstack/charts/tooltip";
import { ErrorBoundary } from "components";
import { ReactNode, useCallback, useMemo } from "react";
import { Loader } from "../../Loader";
import { NoData } from "../../NoData";
import {
  BrandChart,
  ChartExportSvg,
  useBrandChartTheme,
} from "../BrandChart";
import { MIN_HEIGHT } from "../Charts.const";
import {
  BarChartDefinitionOptions,
  buildBarChartDefinition,
} from "./BarChart.definition";
import { BarChartItem } from "./BarChart.types";

export const defaultKeys = ["value"];

/** Row height used to size horizontal charts that don't stretch. */
const HORIZONTAL_BAR_HEIGHT = 40;
const HORIZONTAL_AXIS_SPACE = 60;

export type BarChartProps = {
  orientation: "horizontal" | "vertical";
  isLoading?: boolean;
  data: BarChartItem[];
  /** Only top and right are honoured; guide space is measured automatically. */
  margins?: { t: number; b: number; r: number; l: number };
  stretch?: boolean;
  showXAxis?: boolean;
  noDataMessage?: string;
  valueAxisLabel?: string;
  labelAxisLabel?: string;
  id?: string;
  title?: string;
  exportMode?: true;
  colorRange?: string[];
  minHeight?: string;
  keys?: string[];
  valueNumTicks?: number;
  maxXAxisHeight?: number;
  tooltipRenderer?: (textColor: string, props: BarChartItem) => ReactNode;
};

const BrandBarChart = ({
  orientation,
  isLoading,
  data,
  margins,
  stretch = false,
  showXAxis = true,
  noDataMessage,
  valueAxisLabel,
  labelAxisLabel,
  id,
  title,
  colorRange,
  exportMode = undefined,
  minHeight = MIN_HEIGHT,
  valueNumTicks = 6,
  keys = defaultKeys,
  maxXAxisHeight,
  tooltipRenderer,
}: BarChartProps) => {
  // Exports follow the colour mode too: useExportableChart saves them on the
  // current card background.
  const brandTheme = useBrandChartTheme();

  const options = useMemo(
    (): BarChartDefinitionOptions => ({
      orientation,
      brandTheme,
      valueAxisLabel,
      labelAxisLabel,
      showXAxis,
      valueNumTicks,
      keys,
      colorRange,
      maxXAxisHeight,
      margins: margins ? { t: margins.t, r: margins.r } : undefined,
    }),
    [
      orientation,
      brandTheme,
      valueAxisLabel,
      labelAxisLabel,
      showXAxis,
      valueNumTicks,
      keys,
      colorRange,
      maxXAxisHeight,
      margins,
    ],
  );

  const definition = useMemo(
    () =>
      // Interaction options belong to the responsive config; ones returned
      // from the per-size builder are not applied.
      defineChart({
        chart: ({ width }) => buildBarChartDefinition(data, options, width),
        tooltip: { use: tooltip },
      }),
    [data, options],
  );

  const buildExportDefinition = useCallback(
    ({ width }: { width: number }) =>
      buildBarChartDefinition(data, options, width),
    [data, options],
  );

  if (isLoading) {
    return <Loader minHeight={minHeight} stretch={stretch} />;
  }

  if (!isPopulatedArray(data)) {
    return (
      <NoData message={noDataMessage} height={minHeight} stretch={stretch} />
    );
  }

  const ariaLabel = title ?? valueAxisLabel ?? "Bar chart";

  if (exportMode) {
    return (
      <ChartExportSvg
        id={id}
        title={title}
        ariaLabel={ariaLabel}
        brandTheme={brandTheme}
        buildDefinition={buildExportDefinition}
      />
    );
  }

  const height =
    orientation === "horizontal" && !stretch
      ? `${HORIZONTAL_BAR_HEIGHT * data.length + HORIZONTAL_AXIS_SPACE}px`
      : stretch
        ? "100%"
        : minHeight;

  return (
    <ErrorBoundary>
      <BrandChart
        definition={definition}
        brandTheme={brandTheme}
        ariaLabel={ariaLabel}
        height={height}
        minHeight={minHeight}
        flexGrow={stretch ? 1 : undefined}
        renderTooltipBody={({ points }) => {
          const item = points[0]?.datum;

          if (!item) {
            return null;
          }

          if (tooltipRenderer) {
            return tooltipRenderer(brandTheme.theme.foreground ?? "", item);
          }

          return (
            <Text fontSize="sm">
              <strong>{item.label}:</strong> {toNumberDisplayValue(item.value)}
            </Text>
          );
        }}
      />
    </ErrorBoundary>
  );
};

const BarChart = byLook(BrandBarChart, LegacyBarChart);

export { BarChart };
